import React from 'react';

interface TagConflictAlertProps {
  show: boolean;
  pendingTag: string;
  conflicts: string[];
  onClose: () => void;
}

/**
 * Compact inline conflict callout — renders in-place directly below the tag
 * input row so the user never has to scroll to see it.
 */
const TagConflictAlert: React.FC<TagConflictAlertProps> = ({
  show,
  pendingTag,
  conflicts,
  onClose,
}) => {
  if (!show) return null;

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        marginTop: 'var(--space-2)',
        padding: '10px 14px',
        borderRadius: 'var(--radius-md)',
        background: 'rgba(220,38,38,0.08)',
        border: '1px solid rgba(220,38,38,0.40)',
        fontSize: 'var(--font-size-sm)',
        lineHeight: 1.5,
        animation: 'fadeIn 0.15s ease',
      }}
    >
      {/* ⚠ icon */}
      <span
        aria-hidden="true"
        style={{
          color: 'var(--color-error, #dc2626)',
          fontSize: 16,
          lineHeight: 1.4,
          flexShrink: 0,
        }}
      >
        ⚠
      </span>

      {/* Message */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ color: 'var(--color-error, #dc2626)', display: 'block', marginBottom: 2 }}>
          Cannot add "{pendingTag}"
        </strong>
        <ul
          style={{
            margin: 0,
            paddingLeft: 'var(--space-3)',
            color: 'var(--text-secondary)',
            fontSize: 'var(--font-size-xs)',
          }}
        >
          {conflicts.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
        <p
          style={{
            margin: '4px 0 0 0',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          Remove the conflicting tag first, then try again.
        </p>
      </div>

      {/* OK dismiss button */}
      <button
        type="button"
        onClick={onClose}
        style={{
          flexShrink: 0,
          padding: '3px 10px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid rgba(220,38,38,0.45)',
          background: 'rgba(220,38,38,0.12)',
          color: 'var(--color-error, #dc2626)',
          fontWeight: 600,
          fontSize: 'var(--font-size-xs)',
          cursor: 'pointer',
          lineHeight: 1.6,
          transition: 'background 0.12s',
        }}
        onMouseOver={e =>
          ((e.currentTarget as HTMLButtonElement).style.background = 'rgba(220,38,38,0.22)')
        }
        onMouseOut={e =>
          ((e.currentTarget as HTMLButtonElement).style.background = 'rgba(220,38,38,0.12)')
        }
      >
        OK
      </button>
    </div>
  );
};

export default TagConflictAlert;
